/**
 * ViewSwitcher — conmutador de vistas de un listado (estándar del CMDB).
 *
 * POR QUÉ ESTÁ AQUÍ Y NO EN CADA LISTA
 * ---------------------------------------------------------------------------
 * El conmutador anterior (`ViewToggle`) tenía tres problemas que se veían en
 * todas las pantallas:
 *
 *   1. Pintaba SIEMPRE las tres vistas (fichas, kanban y lista) aunque la lista
 *      solo implementase dos. En Racks, pulsar "kanban" dejaba el área de datos
 *      VACÍA: no era un botón que no se entendía, era un botón que no hacía
 *      nada. De las once listas que lo usaban, solo Servicios tiene kanban.
 *   2. Los colores iban en duro (`#2563eb`, `#fff`, `#cbd5e1`), así que en tema
 *      oscuro y en los skins claros quedaba una píldora blanca pegada.
 *   3. Los botones eran solo iconos, sin texto: había que adivinar qué hacía
 *      cada uno.
 *
 * Este conmutador solo pinta las vistas que el listado declara (`views`), usa
 * las variables del sistema de diseño y lleva el nombre de cada vista, con el
 * icono como apoyo. Si el listado tiene menos de dos vistas no se pinta nada.
 *
 * Cuando la vista activa no es la del componente, aparece un botón para volver
 * a ella. Es útil porque la preferencia se guarda en el usuario: sin ese botón,
 * volver al comportamiento de fábrica exige saber cuál era.
 */

import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

export default function ViewSwitcher({
  views = [],
  value,
  onChange,
  onReset,
  isDefault = true,
  defaultLabel,
}) {
  if (!Array.isArray(views) || views.length < 2) return null;

  return (
    <div className="list-views" role="group" aria-label="Vista del listado">
      {views.map((view) => {
        const Icon = view.icon;
        const active = value === view.key;
        return (
          <button
            key={view.key}
            type="button"
            className={`list-view-btn${active ? ' is-active' : ''}`}
            onClick={() => onChange?.(view.key)}
            aria-pressed={active}
            title={view.title || view.label}
          >
            {Icon && <Icon aria-hidden="true" />}
            <span className="list-view-btn-label">{view.label}</span>
          </button>
        );
      })}

      {!isDefault && onReset && (
        <button
          type="button"
          className="list-view-btn list-view-btn--reset"
          onClick={onReset}
          title={`Volver a la vista por defecto${defaultLabel ? ` (${defaultLabel})` : ''}`}
        >
          <ArrowPathIcon aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
